import React from 'react';
import { ARROW_CORNER_INSET, ARROW_HALF_WIDTH, GAP, VIEWPORT_MARGIN } from '../../constants';
import type { TooltipPresentation } from '../../presentation';

type CalculateTooltipPositionArgs = {
  /** トリガー要素の viewport 座標での矩形 */
  anchorRect: DOMRect;
  /** 計測済みのバブル幅 */
  bubbleWidth: number;
  /** 計測済みのバブル高さ */
  bubbleHeight: number;
  /** viewport の幅 */
  viewportWidth: number;
};

/**
 * ツールチップバブルの表示位置を計算する。
 * トリガー上に収まる余白が無ければ下側に反転する（flip-below）。
 * 左右は viewport 外にはみ出さないよう、実際の幅を計測して clamp する。
 * バブル自体が viewport 端で clamp されトリガー中央からズレた場合でも、
 * 矢印だけはバブルの角丸に衝突しない範囲でトリガー中央に追従させる。
 */
export const calculateTooltipPosition = ({
  anchorRect,
  bubbleWidth,
  bubbleHeight,
  viewportWidth,
}: CalculateTooltipPositionArgs): React.ComponentProps<typeof TooltipPresentation>['position'] => {
  const halfWidth = bubbleWidth / 2;
  const center = anchorRect.left + anchorRect.width / 2;
  const left = Math.min(
    Math.max(center, VIEWPORT_MARGIN + halfWidth),
    viewportWidth - VIEWPORT_MARGIN - halfWidth
  );
  const placement = anchorRect.top < bubbleHeight + GAP + VIEWPORT_MARGIN ? 'bottom' : 'top';
  const top = placement === 'top' ? anchorRect.top : anchorRect.bottom;

  const arrowOffsetLimit = Math.max(0, halfWidth - ARROW_CORNER_INSET - ARROW_HALF_WIDTH);
  const arrowOffsetX = Math.min(Math.max(center - left, -arrowOffsetLimit), arrowOffsetLimit);

  return { top, left, placement, arrowOffsetX };
};
